import React, { useEffect, useState } from "react";
import firebase from "firebase";
import styled from "styled-components";
import { completeTikTokAuth } from "../../../../database/community/tiktokConnection";

// Landing screen for the TikTok OAuth redirect (REDIRECT_PATH =
// /auth/tiktok/callback). The app runs on a HashRouter, so this real path is
// intercepted by pathname in Root — react-router never sees it. TikTok opens
// this in a popup; we read ?code/?state, call tiktokCompleteAuth (which carries
// the admin's Firebase auth via shared LOCAL persistence), then close the popup
// so the Media tab's focus listener refreshes the connection.

const Screen = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111111;
  color: #ffffff;
  font-family: var(--font-sans, system-ui, sans-serif);
  padding: 24px;
`;

const Card = styled.div`
  max-width: 380px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const Body = styled.div`
  font-size: 14px;
  color: #aaaaaa;
  line-height: 1.5;
`;

type Phase = "working" | "done" | "error";

const TikTokCallback: React.FC = () => {
  const [phase, setPhase] = useState<Phase>("working");
  const [message, setMessage] = useState("Finishing your TikTok connection…");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code") || "";
    const state = params.get("state") || "";
    const oauthError = params.get("error") || params.get("error_description");

    if (oauthError) {
      setPhase("error");
      setMessage(`TikTok returned an error: ${oauthError}`);
      return;
    }
    if (!code || !state) {
      setPhase("error");
      setMessage("Missing code or state from TikTok. Try connecting again.");
      return;
    }

    // Wait for Firebase to resolve the signed-in admin (the callable is
    // admin-gated). Auth persists across tabs/popups, so this fires quickly.
    let handled = false;
    const unsub = firebase.auth().onAuthStateChanged(async (user) => {
      if (handled) return;
      if (!user) {
        // Not signed in yet in this popup context — keep waiting briefly; if it
        // never resolves the admin can retry from the Media tab.
        return;
      }
      handled = true;
      unsub();
      try {
        const res = await completeTikTokAuth({ code, state });
        setPhase("done");
        setMessage(
          res.connected
            ? "TikTok connected. You can close this window."
            : "Connection finished. You can close this window."
        );
        // If this is a popup, close it so the opener refreshes on focus.
        setTimeout(() => {
          if (window.opener) window.close();
        }, 1200);
      } catch (e: any) {
        setPhase("error");
        setMessage(e?.message || "Could not finish the TikTok connection.");
      }
    });

    return () => unsub();
  }, []);

  return (
    <Screen>
      <Card>
        <Title>
          {phase === "working"
            ? "Connecting TikTok…"
            : phase === "done"
            ? "All set"
            : "Something went wrong"}
        </Title>
        <Body>{message}</Body>
        {phase !== "working" ? (
          <Body>
            <a href="/" style={{ color: "#fff" }}>
              Back to the admin
            </a>
          </Body>
        ) : null}
      </Card>
    </Screen>
  );
};

export default TikTokCallback;
